import React, { useState, useEffect } from 'react';
import { auditApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { ClockCounterClockwise, UserCircle } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const ACTION_COLORS = {
  create: 'bg-emerald-100 text-emerald-700',
  update: 'bg-sky-100 text-sky-700',
  delete: 'bg-red-100 text-red-700',
  login: 'bg-violet-100 text-violet-700',
  submit: 'bg-blue-100 text-blue-700',
  approve: 'bg-green-100 text-green-700',
  deny: 'bg-rose-100 text-rose-700',
};

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('all');

  useEffect(() => { loadLogs(); }, [entityFilter]);

  const loadLogs = async () => {
    try {
      const params = { limit: 200 };
      if (entityFilter !== 'all') params.entity_type = entityFilter;
      const res = await auditApi.list(params);
      setLogs(res.data);
    } catch (err) { toast.error('Failed to load audit logs'); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div></div>;

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      <Toaster position="top-right" richColors />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 font-['Outfit']" data-testid="audit-title">Audit Logs</h1>
          <p className="text-sm text-slate-500 mt-1">Track all system activities and changes</p>
        </div>
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger className="w-44 border-slate-200" data-testid="entity-filter">
            <SelectValue placeholder="All Entities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Entities</SelectItem>
            <SelectItem value="claim">Claims</SelectItem>
            <SelectItem value="payment">Payments</SelectItem>
            <SelectItem value="preauth">Pre-Auth</SelectItem>
            <SelectItem value="denial">Denials</SelectItem>
            <SelectItem value="document">Documents</SelectItem>
            <SelectItem value="user">Users</SelectItem>
            <SelectItem value="tenant">Hospitals</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="audit-table">
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ClockCounterClockwise className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">No audit logs recorded yet</p>
              <p className="text-xs mt-1">Activities will be logged as users interact with the system</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Time</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">User</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Action</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Entity</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} className="hover:bg-slate-50 transition-colors" data-testid={`audit-row-${log.id}`}>
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-7 w-7 rounded-full bg-gradient-to-br from-sky-500 to-violet-500 flex items-center justify-center text-white text-[10px] font-semibold">
                            {(log.user_name || '?').charAt(0)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-700">{log.user_name || 'System'}</p>
                            <p className="text-[10px] text-slate-400">{log.user_email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-600'} border-0 text-xs`}>{log.action}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize">{log.entity_type}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-xs truncate">{log.details}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogsPage;
